import React, { useState } from "react";
import { Box, Button, Alert, Snackbar, useTheme, Chip, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import { gql, useMutation, useQuery } from "@apollo/client";
import { formatDisplayDate } from "../../utils/date";

const GET_USER_GUARANTEES = gql`
  query {
    userGuaranteedLoans {
      id
      guarantorId
      loanApplicationId
      applicantUserId
      applicantFirstName
      applicantLastName
      amountPledged
      amountPaidBack
      expectedInterestShare
      approvedByGuarantor
      createdDate
    }
  }
`;

const APPROVE_GUARANTORSHIP = gql`
  mutation ApproveGuarantorship($guarantorId: Int, $loanGuarantorId: Int!) {
    approveGuarantorship(
      input: { guarantorId: $guarantorId, loanGuarantorId: $loanGuarantorId }
    ) {
      errorMessage
      status
    }
  }
`;

const MyGuarantees = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });
  const [approvingRowId, setApprovingRowId] = useState(null);
  const { data, loading, error, refetch } = useQuery(GET_USER_GUARANTEES);
  const [approveGuarantorship] = useMutation(APPROVE_GUARANTORSHIP);

  const showToast = (message, severity = "info") => {
    setToast({ open: true, message, severity });
  };

  const handleApprove = async (row) => {
    try {
      setApprovingRowId(row.id);
      const { data: response } = await approveGuarantorship({
        variables: {
          guarantorId: row.guarantorId || null,
          loanGuarantorId: Number(row.id),
        },
      });

      const result = response?.approveGuarantorship;

      if (result?.status === "Success") {
        showToast("Guarantee approved successfully.", "success");
        await refetch();
      } else {
        showToast(result?.errorMessage || "Could not approve guarantee.", "error");
      }
    } catch (e) {
      showToast(e?.message || "Could not approve guarantee.", "error");
    } finally {
      setApprovingRowId(null);
    }
  };

  const columns = [
    { field: "sequence", headerName: "#", width: 70, sortable: false, filterable: false },
    { field: "loanApplicationId", headerName: "Loan ID", flex: 0.8 },
    {
      field: "applicant",
      headerName: "Applicant",
      flex: 1,
      valueGetter: ({ row }) =>
        `${row.applicantFirstName || ""} ${row.applicantLastName || ""}`.trim(),
    },
    {
      field: "amountPledged",
      headerName: "Amount Requested",
      flex: 1,
      valueFormatter: ({ value }) => (value != null ? `KES ${Number(value).toLocaleString()}` : "-"),
    },
    {
      field: "amountPaidBack",
      headerName: "Amount Paid Back",
      flex: 1,
      valueFormatter: ({ value }) => (value != null ? `KES ${Number(value).toLocaleString()}` : "-"),
    },
    {
      field: "expectedInterestShare",
      headerName: "Interest Share",
      flex: 1,
      valueFormatter: ({ value }) => (value != null ? `KES ${Number(value).toLocaleString()}` : "-"),
    },
    {
      field: "approvedByGuarantor",
      headerName: "Approved?",
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Approved" : "Pending"}
          size="small"
          color={value ? "success" : "warning"}
          variant={value ? "filled" : "outlined"}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: "createdDate",
      headerName: "Date Guaranteed",
      flex: 1,
      valueFormatter: ({ value }) => formatDisplayDate(value),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        row.approvedByGuarantor ? (
          "Approved"
        ) : (
          <Button
            size="small"
            variant="contained"
            onClick={() => handleApprove(row)}
            disabled={approvingRowId === row.id}
          >
            {approvingRowId === row.id ? "Approving..." : "Approve"}
          </Button>
        ),
    },
  ];

  if (error) return <p>Error: {error.message}</p>;

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="MY GUARANTEES" subtitle="Loans You Have Guaranteed" />
      <Box mt="40px" height="75vh" sx={{
        "& .MuiDataGrid-root": { border: "none" },
        "& .MuiDataGrid-cell": { borderBottom: "none" },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.secondary[100],
          borderBottom: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: theme.palette.primary.light,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.secondary[100],
          borderTop: "none",
        },
        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
          color: `${theme.palette.secondary[200]} !important`,
        },
      }}>
        <DataGrid
          loading={loading}
          rows={(data?.userGuaranteedLoans || []).map((row, index) => ({ ...row, sequence: index + 1 }))}
          getRowId={(row) => row.id}
          columns={columns}
        
          columnVisibilityModel={
            isMobile
              ? {
                loanApplicationId: false,
                amountPaidBack: false,
                expectedInterestShare: false,
                createdDate: false,
              }
              : undefined
          }/>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyGuarantees;
